/** Dedicated results route — count, active filters, sort, cards. Nothing
 * else before the listings (User-Brief 22.09.2026). Back restores scroll. */
import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, SlidersHorizontal, X } from "lucide-react";
import { getPublicFeed } from "../api/publicApi";
import DarkSelect from "../components/home/DarkSelect";
import PublicDealCard from "../components/public/PublicDealCard";
import useRecentSearches from "../hooks/useRecentSearches";
import { useAuth } from "../auth/AuthContext";
import { trackFunnel } from "../lib/funnel";
import {
  activeCriteria, paramsToQuery, queryToParams, removeCriterion, summarizeSearch,
} from "../lib/searchQuery";

export default function SearchResultsPage() {
  const { vertical: vParam } = useParams();
  const vertical = vParam === "auto" ? "auto" : "immo";
  const { t } = useTranslation(["discover", "enums"]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => queryToParams(searchParams.toString()), [searchParams]);
  const { record } = useRecentSearches();

  const queryParams = useMemo(() => {
    const p = { limit: 24 };
    Object.entries(params).forEach(([k, v]) => {
      if (k !== "location_label" && v !== null && v !== "" && v !== undefined) p[k] = v;
    });
    return p;
  }, [params]);

  const feedQ = useInfiniteQuery({
    queryKey: ["public-feed", vertical, queryParams],
    queryFn: async ({ pageParam }) => getPublicFeed(vertical, { ...queryParams, ...(pageParam || {}) }),
    initialPageParam: null,
    getNextPageParam: (last) => {
      if (last?.next_cursor) return { cursor: last.next_cursor };
      if (last?.next_page) return { page: last.next_page };
      return undefined;
    },
    staleTime: 60_000,
  });
  const items = (feedQ.data?.pages || []).flatMap((p) => p.items || []);
  const meta = feedQ.data?.pages?.[0]?.meta;

  // EXECUTED search -> history (dedupe server/local); not every form change.
  const recordedRef = useRef(null);
  useEffect(() => {
    const key = `${vertical}|${searchParams.toString()}`;
    if (recordedRef.current === key) return;
    recordedRef.current = key;
    record({ vertical, label: summarizeSearch(vertical, params, t), params });
    trackFunnel("search_executed", { vertical, authed: !!user });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertical, searchParams]);

  // Scroll restore when returning from a listing.
  const scrollKey = `da_results_scroll:${vertical}:${searchParams.toString()}`;
  useEffect(() => {
    const saved = Number(sessionStorage.getItem(scrollKey) || 0);
    if (saved > 0 && !feedQ.isLoading && items.length) {
      window.scrollTo(0, saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedQ.isLoading]);

  const openCard = (card) => {
    try { sessionStorage.setItem(scrollKey, String(window.scrollY)); } catch { /* noop */ }
    trackFunnel("deal_detail_open", { ulid: card.id, authed: !!user });
    navigate(user ? `/listing/${card.id}` : `/deal/${card.id}`);
  };

  const updateParams = (next) => {
    setSearchParams(new URLSearchParams(paramsToQuery(next)), { replace: false });
  };

  const chips = activeCriteria(vertical, params, t);
  const sortOptions = [
    { value: "recommended", label: t("sort.recommended") },
    { value: "newest", label: t("sort.newest") },
    { value: "price_asc", label: t("sort.priceAsc") },
    { value: "price_desc", label: t("sort.priceDesc") },
    ...(vertical === "auto" ? [{ value: "mileage_asc", label: t("sort.mileageAsc") }] : []),
    { value: "discount", label: t("sort.discount") },
  ];
  const countLabel = !meta
    ? null
    : (meta.total_unavailable
      // Ehrlich: Treffer stehen, die Gesamtzahl war nicht zählbar.
      ? t("feed.matchCountUnknown")
      : (meta.total_capped
        ? t("feed.matchCountCapped", { count: (2000).toLocaleString("de-AT") })
        : t("feed.matchCount", { count: (meta.total_matches ?? 0).toLocaleString("de-AT") })));

  return (
    <div className="min-h-dvh bg-zinc-950 text-zinc-100" data-testid={`results-page-${vertical}`}>
      <div className="sticky top-0 z-20 border-b border-zinc-800/80 bg-zinc-950/95 backdrop-blur">
        <div className="mx-auto max-w-7xl space-y-2 px-3 py-2.5 sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="results-edit-filters"
              onClick={() => navigate(`/search/${vertical}?${paramsToQuery(params)}`)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 hover:border-zinc-500"
            >
              <SlidersHorizontal size={13} aria-hidden /> {t("results.editFilters")}
            </button>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold" data-testid="results-summary">
                {summarizeSearch(vertical, params, t)}
              </div>
              {countLabel && (
                <div className="text-[11px] text-zinc-500" data-testid="results-count">{countLabel}</div>
              )}
            </div>
            <div className="flex w-full items-center justify-end gap-2 sm:ml-auto sm:w-auto">
              <button
                type="button"
                data-testid="results-deals-toggle"
                onClick={() => updateParams({ ...params, quality: params.quality === "deals" ? "all" : "deals" })}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                  params.quality === "deals"
                    ? "border-emerald-500/70 bg-emerald-500/15 text-emerald-300"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                }`}
              >
                {t("quick.dealsOnly")}
              </button>
              <div className="w-40 sm:w-48">
                <DarkSelect
                  compact
                  testid="results-sort"
                  value={params.sort || "recommended"}
                  options={sortOptions}
                  onChange={(v) => updateParams({ ...params, sort: v })}
                />
              </div>
            </div>
          </div>
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="results-active-chips">
              {chips.map((c) => (
                <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-zinc-800/80 px-2.5 py-1 text-[11px] text-zinc-300">
                  {c.label}
                  <button
                    type="button"
                    aria-label={`remove-${c.key}`}
                    data-testid={`chip-remove-${c.key}`}
                    onClick={() => updateParams(removeCriterion(params, c.key))}
                    className="text-zinc-500 hover:text-zinc-200"
                  >
                    <X size={11} aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-3 py-4 sm:px-6">
        {feedQ.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-zinc-500">
            <Loader2 size={16} className="animate-spin" aria-hidden /> {t("feed.loading")}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-8 text-center" data-testid="results-empty">
            <p className="text-sm text-zinc-400">{t("results.emptyTitle")}</p>
            {chips.length > 0 && (
              <>
                <p className="mt-2 text-xs text-zinc-500">{t("results.emptyHint")}</p>
                <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                  {chips.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      data-testid={`empty-remove-${c.key}`}
                      onClick={() => updateParams(removeCriterion(params, c.key))}
                      className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:border-emerald-500/60"
                    >
                      <X size={11} aria-hidden /> {c.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="results-grid">
            {items.map((card, i) => (
              <PublicDealCard key={`${card.id}-${i}`} card={card} index={i} onOpen={openCard} />
            ))}
          </div>
        )}
        {feedQ.hasNextPage && (
          <div className="flex justify-center pt-4">
            <button
              type="button"
              data-testid="results-load-more"
              onClick={() => feedQ.fetchNextPage()}
              disabled={feedQ.isFetchingNextPage}
              className="rounded-xl border border-zinc-700 px-5 py-2.5 text-sm font-medium text-zinc-300 hover:border-zinc-500 disabled:opacity-50"
            >
              {feedQ.isFetchingNextPage ? t("feed.loading") : t("feed.loadMore")}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
